import { useState, type FormEvent } from 'react';

import { ApiError } from '../api.ts';

interface SignInProps {
  readonly onSignIn: (email: string, password: string) => Promise<void>;
  readonly stage: string;
}

/**
 * Baseline sign-in.
 *
 * The failure message is identical for an unknown address and a wrong password, because
 * the server makes no distinction either — the pool has `PreventUserExistenceErrors`
 * enabled, so telling them apart here would mean inventing information the server
 * refused to give.
 */
export function SignIn({ onSignIn, stage }: SignInProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(undefined);
    try {
      await onSignIn(email.trim(), password);
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : 'Could not reach the server. Check that the BFF is running.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="signin">
      <p className="eyebrow">AnyCompany Hotels · step-up reference · {stage}</p>
      <h2>Sign in to your bookings</h2>
      <p className="muted">
        Use one of the synthetic accounts from <code className="value">.demo-credentials.json</code>
        . No real customer data exists in this environment.
      </p>

      <form onSubmit={submit} noValidate>
        <div className="field">
          <label htmlFor="email">Email address</label>
          <input
            id="email"
            name="email"
            type="email"
            autoComplete="username"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </div>

        <div className="field">
          <label htmlFor="password">Password</label>
          <input
            id="password"
            name="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </div>

        {/*
          Announced rather than merely rendered. A sighted user sees the message appear
          next to the button; without a live region a screen reader user would be left
          with a form that simply did not submit.
        */}
        <div role="alert" aria-live="assertive">
          {error ? (
            <div className="notice notice--refused">
              <p>{error}</p>
            </div>
          ) : null}
        </div>

        <div className="actions">
          <button type="submit" disabled={busy || email === '' || password === ''}>
            {busy ? 'Signing in\u2026' : 'Sign in'}
          </button>
        </div>
      </form>
    </main>
  );
}
